import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FolderOpen, 
  PlusCircle,
  Users,
  Database,
  Puzzle,
  Webhook,
  Settings,
  Trash2,
  Brain,
  Mail,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Circle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sidebar = () => {
  const { isSuperAdmin } = useAuth();
  const location = useLocation();
  const [statutsProgression, setStatutsProgression] = useState([]);
  const [dossiersExpanded, setDossiersExpanded] = useState(false);
  const [dossiersCounts, setDossiersCounts] = useState({});

  // Ouvrir automatiquement si on est dans une section dossiers
  useEffect(() => {
    if (location.pathname.includes('/dossiers')) {
      setDossiersExpanded(true);
    }
  }, [location.pathname]);

  // Charger les statuts de progression depuis la BDD
  useEffect(() => {
    const loadStatuts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/statuts-progression`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setStatutsProgression(data || []);
        }
      } catch (e) {
        console.error('Erreur chargement statuts:', e);
      }
    };
    
    loadStatuts();
  }, []);

  // Charger les compteurs pour chaque statut
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/dossiers/counts-by-statut`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setDossiersCounts(data || {});
        }
      } catch (e) {
        console.error('Erreur comptage:', e);
      }
    };
    
    loadCounts();
    const interval = setInterval(loadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Items principaux
  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
  ];
  
  // Générer les sous-items dynamiquement depuis les statuts
  const generateDossierSubItems = () => {
    const items = [
      { to: '/dossiers', icon: FolderOpen, label: 'Tous', exact: true, count: dossiersCounts.total }
    ];
    
    // Ajouter chaque statut de la BDD (sans couleur, juste le label)
    statutsProgression.forEach(statut => {
      items.push({
        to: `/dossiers/statut/${statut.code}`,
        label: statut.label,
        count: dossiersCounts[statut.code] || 0
      });
    });
    
    // Ajouter la corbeille à la fin
    items.push({ to: '/dossiers/corbeille', icon: Trash2, label: 'Corbeille' });
    
    return items;
  };
  
  const dossierSubItems = generateDossierSubItems();
  
  const otherNavItems = [
    { to: '/nouveau-dossier', icon: PlusCircle, label: 'Nouvelle demande' },
    { to: '/messagerie', icon: Mail, label: 'Messagerie', highlight: 'emerald' },
    { to: '/registre', icon: Database, label: 'Registre' },
    { to: '/parcours', icon: GitBranch, label: 'Parcours Organismes', highlight: 'blue' },
  ];

  const adminNavItems = [];
  if (isSuperAdmin) {
    adminNavItems.push({ to: '/super-admin', icon: Users, label: 'Gestion Équipe' });
    adminNavItems.push({ to: '/widgets', icon: Puzzle, label: 'Widgets' });
    adminNavItems.push({ to: '/webhooks', icon: Webhook, label: 'API & Webhooks' });
    adminNavItems.push({ to: '/configuration', icon: Settings, label: 'Configuration' });
    adminNavItems.push({ to: '/super-agent', icon: Brain, label: 'Agents IA', highlight: 'purple' });
  }

  const linkClasses = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
      isActive
        ? 'bg-green-50 text-green-700 font-semibold'
        : 'text-gray-600 hover:bg-gray-100'
    }`;

  const renderNavItem = (item) => {
    const Icon = item.icon;
    
    if (item.highlight === 'purple') {
      return (
        <NavLink 
          key={item.to} 
          to={item.to} 
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
              isActive
                ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold shadow-lg'
                : 'bg-gradient-to-r from-violet-50 to-purple-50 text-purple-700 hover:from-violet-100 hover:to-purple-100 font-medium'
            }`
          }
        >
          <Icon className="w-5 h-5" />
          <span>{item.label}</span>
        </NavLink>
      );
    }
    
    if (item.highlight === 'emerald') {
      return (
        <NavLink 
          key={item.to} 
          to={item.to} 
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
              isActive
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold shadow-lg'
                : 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 hover:from-emerald-100 hover:to-teal-100 font-medium'
            }`
          }
        >
          <Icon className="w-5 h-5" />
          <span>{item.label}</span>
        </NavLink>
      );
    }
    
    if (item.highlight === 'blue') {
      return (
        <NavLink 
          key={item.to} 
          to={item.to} 
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
              isActive
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold shadow-lg'
                : 'bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 hover:from-blue-100 hover:to-cyan-100 font-medium'
            }`
          }
        >
          <Icon className="w-5 h-5" />
          <span>{item.label}</span>
        </NavLink>
      );
    }
    
    // Lien avec badge (nouveaux non vus)
    if (item.badge > 0) {
      return (
        <NavLink 
          key={item.to} 
          to={item.to} 
          end={item.exact}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
              isActive
                ? 'bg-red-100 text-red-700 font-semibold'
                : 'bg-red-50 text-red-600 hover:bg-red-100 font-medium'
            }`
          }
        >
          <Icon className="w-5 h-5" />
          <span>{item.label}</span>
          <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
            {item.badge}
          </span>
        </NavLink>
      );
    }
    
    return (
      <NavLink key={item.to} to={item.to} end={item.exact} className={linkClasses}>
        <Icon className="w-5 h-5" />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="w-60 bg-white border-r border-gray-200 h-[calc(100vh-57px)] overflow-y-auto">
      <nav className="p-3 space-y-1">
        {/* Tableau de bord */}
        {navItems.map(renderNavItem)}
        
        {/* Section Dossiers avec sous-menu */}
        <div>
          <button
            onClick={() => setDossiersExpanded(!dossiersExpanded)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
              location.pathname.includes('/dossiers')
                ? 'bg-green-50 text-green-700 font-semibold'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FolderOpen className="w-5 h-5" />
            <span>Dossiers</span>
            {dossiersCounts.total > 0 && (
              <span className="bg-gray-200 text-gray-700 text-xs px-1.5 py-0.5 rounded-full font-bold">
                {dossiersCounts.total}
              </span>
            )}
            <span className="ml-auto">
              {dossiersExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          </button>
          
          {/* Sous-menu dossiers - Dynamique */}
          {dossiersExpanded && (
            <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-200 pl-3">
              {dossierSubItems.map((item) => {
                const Icon = item.icon || Circle;
                
                return (
                  <NavLink 
                    key={item.to} 
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                        isActive
                          ? 'bg-green-50 text-green-700 font-semibold'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`
                    }
                  >
                    {item.icon ? (
                      <Icon className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 flex items-center justify-center text-xs">•</span>
                    )}
                    <span className="truncate">{item.label}</span>
                    {item.count > 0 && (
                      <span className="ml-auto bg-gray-200 text-gray-700 text-xs px-1.5 rounded-full font-medium">
                        {item.count}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Autres items */}
        {otherNavItems.map(renderNavItem)}
        
        {/* Items admin */}
        {adminNavItems.length > 0 && (
          <>
            <div className="border-t border-gray-200 my-2"></div>
            {adminNavItems.map(renderNavItem)}
          </>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
